<script lang="ts">
  export interface Column {
    key: string;
    label: string;
    width?: string;
    render?: (val: any) => string;
  }

  interface Props {
    columns: Column[];
    rows: any[];
    loading?: boolean;
  }

  let { columns, rows, loading = false }: Props = $props();

  let sortKey = $state('');
  let sortAsc = $state(true);

  const sortedRows = $derived.by(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortAsc ? cmp : -cmp;
    });
  });

  function toggleSort(key: string) {
    if (sortKey === key) {
      sortAsc = !sortAsc;
    } else {
      sortKey = key;
      sortAsc = true;
    }
  }

  function isMonoField(key: string): boolean {
    const lower = key.toLowerCase();
    return lower.includes('id') || lower.includes('timestamp') || lower.includes('time') || lower.includes('arn') || lower.includes('date');
  }
</script>

<div class="overflow-auto rounded-lg border border-border bg-bg-panel">
  <table class="w-full text-sm text-left">
    <thead>
      <tr class="border-b border-border bg-bg-dark/50">
        {#each columns as col (col.key)}
          <th
            class="px-4 py-2.5 text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer select-none hover:text-text-primary transition-colors whitespace-nowrap"
            style={col.width ? `width: ${col.width}` : ''}
            onclick={() => toggleSort(col.key)}
            aria-sort={sortKey === col.key ? (sortAsc ? 'ascending' : 'descending') : 'none'}
          >
            <span class="inline-flex items-center gap-1">
              {col.label}
              {#if sortKey === col.key}
                <span class="text-accent">{sortAsc ? '↑' : '↓'}</span>
              {/if}
            </span>
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#if loading}
        {#each Array(5) as _, i (i)}
          <tr class="border-b border-border-light">
            {#each columns as col (col.key)}
              <td class="px-4 py-3">
                <div class="h-4 rounded bg-bg-card animate-skeleton-pulse" style="width: {60 + Math.random() * 30}%"></div>
              </td>
            {/each}
          </tr>
        {/each}
      {:else if sortedRows.length === 0}
        <tr>
          <td colspan={columns.length} class="px-4 py-12 text-center text-text-muted">
            <div class="flex flex-col items-center gap-2">
              <svg class="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <span class="text-sm">No data</span>
            </div>
          </td>
        </tr>
      {:else}
        {#each sortedRows as row, i (i)}
          <tr class="border-b border-border-light hover:bg-bg-hover transition-colors {i % 2 === 1 ? 'bg-bg-dark/20' : ''}">
            {#each columns as col (col.key)}
              <td class="px-4 py-2.5 text-text-primary {isMonoField(col.key) ? 'font-mono text-xs' : ''}">
                {#if col.render}
                  {@html col.render(row[col.key])}
                {:else}
                  {row[col.key] ?? '—'}
                {/if}
              </td>
            {/each}
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>
